import React, {
  useCallback, useReducer, useState, useContext, useEffect, useMemo,
} from 'react';
import { gql, useQuery } from '@apollo/client';
import { BubbleRecommendations, MicroFrontend, useLoudCrowdScript } from '@xp-utilities/web';
import AddToBagForm from '../../containers/AddToBagForm/AddToBagForm';
import ProductPageReducer from './ProductPageReducer';
import { ERROR_MESSAGE, LOADING_MESSAGE } from '../../components/Messages/Messages';
import textQueries from './textQueries';
import productQueries from './productQueries';
import ProductPageContext from '../../contexts/ProductPageContext';
import SizeGuideModal from '../../components/SizeGuideModal/SizeGuideModal';
import MainImages from '../../features/Images/MainImages';
import ProductNameAndDescription from '../../components/ProductNameAndDescription/ProductNameAndDescription';
import BreakpointContext from '../../contexts/BreakpointContext';
import ProductRatingReviews from '../../components/ProductRatingReviews/ProductRatingReviews';
import ReviewsLegalText from '../../features/ProductReviews/ReviewsLegalText';
import AnalyticsTriggers from '../../helpers/analyticsTriggers';
import Stylitics from '../../features/Stylitics/Stylitics';
import ESpotContent from '../../components/ESpotContent/ESpotContent';
import SwitchTestContext from '../../contexts/SwitchTestContext';
import SoldOutPage from '../SoldOutPage/SoldOutPage';
import BreadCrumbsList from '../../components/BreadCrumbsList/BreadCrumbsList';
import MeasurementsAndStyliticsOverlay from '../../features/Images/MeasurementsAndStyliticsOverlay';
import SeoSchema from '../../components/SeoSchema/SeoSchema';
import BrowserContext from '../../contexts/BrowserContext';
import { checkHasShowStyliticsEnabled } from '../../helpers/styliticsUtils';
import useIsExternalUser from '../../tools/customHooks/useIsExternalUser';
import ReviewScript from '../../features/ProductReviews/ReviewScript';

const getCollectionData = gql`
query Collection(
  $collectionId: String!,
  $productId: String!,
  $categoryId: String!,
  $faceout: String
) {
  textFor {
    ${textQueries}
  }
  collection(collectionId: $collectionId, productId: $productId, faceout: $faceout) {
    collectionApiSuccess
    collection {
      ${productQueries}
    }
  }
  reviewData: bazaarVoice(collectionId: $collectionId, productId: $productId) {
    fitRecommendation
    showFitRecommendation
    hasBazaarVoiceEnable
    averageOverallRating
    reviewsSku
    seoReviewsData {
      userNickname,
      rating,
      datePublished,
      title,
      reviewText,
      avgProductRating,
      totalProductReview,
    }
  }
  shippingContent {
    hasSimpleReturns
  }
  config {
    customerServiceUrl: string(name: "customerServiceUrl")
  }
  content {
    pdpESpots(collectionId: $collectionId, categoryId: $categoryId) {
      pdp_sizefitguide { content key source }
      pdp_comparelengths { content key source }
      pdp_productdetails { content key source }
      pdp_afterproducttitle { content key source }
      pdp_beforeproducttitle { content key source }
      pdp_sizefitaccordion { content key source }
      pdp_aboveaccordion { content key source }
      pdp_aftersaveforlater { content key source }
      pdp_popins { content key source }
      pdp_belowthefold1 { content key source }
      pdp_belowthefold2 { content key source }
      pdp_howtomeasure { content key source }
      pdp_beforeaddtobag { content key source }
    }
    legacyPDPESpots(productId: $productId) {
      ProductPromoEMS { content key source }
      pdp_sizeGuideHowToMeasure { content key source }
    }
  }
}
`;

const setPDPDigitalDataObject = (state) => {
  if (!window?.digitalData) return;
  if (!state.collectionData) return;

  const { collectionData } = state;
  const { activeProduct = { kicId: '' }, breadcrumbsUrls = [] } = collectionData;

  window.digitalData.set('pdp.collectionData', {
    activeProduct: { kicId: activeProduct?.kicId },
    breadcrumbsUrls,
  });
  window.digitalData.set('pdp.suggestedSearch', activeProduct.suggestedSearchAttributes);
};

export default function ProductPageContent({
  collectionId,
  productId,
  categoryId,
  faceout,
  store,
  storeId,
  currency,
}) {
  const [openSizeGuide, setSizeGuideOpen] = useState(false);

  const setActiveProdId = (newProductId) => {
    dispatch({
      type: 'UPDATE_ACTIVE_PRODUCT',
      payload: newProductId,
    });
  };

  const setSelectedPrimarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'primary',
        value: selectedSize,
        isSticky: false,
      },
    });
  };

  const setSelectedSecondarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'secondary',
        value: selectedSize,
        isSticky: false,
      },
    });
  };

  const setStickySelectedPrimarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'primary',
        value: selectedSize,
        isSticky: true,
      },
    });
  };

  const setStickySelectedSecondarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'secondary',
        value: selectedSize,
        isSticky: true,
      },
    });
  };

  const setActiveProductHasSocialProofing = (hasSocialProofing = false) => {
    dispatch({
      type: 'UPDATE_ACTIVE_PRODUCT_SOCIAL_PROOFING',
      payload: {
        hasSocialProofing,
      },
    });
  };

  const intialState = {
    collectionId,
    productId,
    categoryId,
    store,
    storeId,
    collectionData: null,
    actions: {
      setActiveProductHasSocialProofing,
    },
  };

  const swatchChanged = useCallback(({ target: { value } }) => setActiveProdId(value), []);
  const [ProductPageState, dispatch] = useReducer(ProductPageReducer, intialState);
  const breakpoints = useContext(BreakpointContext);
  const SwitchTestState = useContext(SwitchTestContext);
  const isBrowser = useContext(BrowserContext);

  const isMobile = breakpoints.xxsmall && !breakpoints.medium;
  const brand = ProductPageState.collectionData?.activeProduct?.brand;
  const kicId = ProductPageState.collectionData?.activeProduct?.kicId;
  const siteRegion = ProductPageState.collectionData?.siteRegion;
  const hasPDPMFEBelowTheFold = ProductPageState.collectionData?.hasPDPMFEBelowTheFold;
  const hasBaazarVoice = ProductPageState.reviewData?.hasBazaarVoiceEnable;
  const hasProductMfeSeoSchema = ProductPageState.collectionData?.hasProductMfeSeoSchema;
  const hasProductRecsStoreAttr = ProductPageState.collectionData?.hasProductRecsStoreAttr;
  const showReviewsLegalText = ProductPageState.collectionData?.hasReviewsLegalText;
  const showStyliticsFromFeatureSwitch = SwitchTestState['pdp-hotspots-outfitting-solution'];
  const enableLoudCrowdScript = SwitchTestState['pdp-enable-loudcrowd-script'];
  const hasCategoryRecs = SwitchTestState['pdp-has-category-recs'];
  const hasStyliticsQuickViewMfeFlag = SwitchTestState['quickview-mfe-product-stylitics'];
  const hasBazaarvoiceMFEEnabled = ProductPageState?.collectionData?.hasBazaarvoiceMFEEnabled ?? false;
  const renderMfeReviewScript = hasBaazarVoice && hasBazaarvoiceMFEEnabled;
  const isExternalUser = useIsExternalUser(ProductPageState);

  const hasShowStyliticsEnabled = checkHasShowStyliticsEnabled(
    showStyliticsFromFeatureSwitch,
    brand,
    isMobile,
  );

  // Influencer tools for affiliate storefronts
  useLoudCrowdScript({
    enable: enableLoudCrowdScript,
    shopId: ProductPageState.collectionData?.loudCrowdShopId,
  });

  const {
    error, loading, data, previousData,
  } = useQuery(getCollectionData, {
    variables: {
      collectionId: `${collectionId}`,
      productId: `${productId}`,
      faceout,
      categoryId: `${categoryId}`,
    },
    context: { batch: true },
  });

  const styliticsComponent = useMemo(() => {
    if (!hasShowStyliticsEnabled) return null;

    return (
      <Stylitics
        kicId={kicId}
        brand={brand}
        tmntData={ProductPageState.tmntData}
        isMobile={isMobile}
        swatchChanged={swatchChanged}
        siteRegion={siteRegion}
        locale={ProductPageState.collectionData?.styliticsV3Locale}
        quickViewMfeFlag={hasStyliticsQuickViewMfeFlag}
      />
    );
  }, [
    hasShowStyliticsEnabled,
    kicId,
    brand,
    ProductPageState.tmntData,
    isMobile,
    swatchChanged,
    siteRegion,
    ProductPageState.collectionData?.styliticsV3Locale,
    hasStyliticsQuickViewMfeFlag,
  ]);

  const titleWrapper = ({ children }) => (
    <h2 className={`bubble-recommendations-title ${brand}`}>{children}</h2>
  );

  useEffect(() => {
    if (!hasPDPMFEBelowTheFold) {
      window.digitalData.trigger('mfe-pdp-page-loaded');
    }
    AnalyticsTriggers.productDetail(ProductPageState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ProductPageState) return;
    setPDPDigitalDataObject(ProductPageState);
  }, [ProductPageState]);

  useEffect(() => {
    const handleSizeGuideOpen = () => {
      setSizeGuideOpen(true);
    };

    const sizeGuideButton = document.querySelector('.genericESpot [data-modal="size-guide-modal"]');
    if (sizeGuideButton) {
      sizeGuideButton.addEventListener('click', handleSizeGuideOpen);
    }

    return () => {
      if (sizeGuideButton) {
        sizeGuideButton.removeEventListener('click', handleSizeGuideOpen);
      }
    };
  }, [ProductPageState.content?.pdpESpots?.pdp_belowthefold1]);

  const queryData = data || previousData;

  if (queryData && queryData.collection.collection && queryData.collection.collection.isSoldOut) {
    return <SoldOutPage queryData={queryData} collectionId={collectionId} />;
  }

  if (queryData && queryData.collection && !ProductPageState.collectionData) {
    dispatch({
      type: 'INITIAL_QUERY_COMPLETE',
      payload: queryData,
    });
  }

  if (error) {
    return ERROR_MESSAGE;
  }

  if (!queryData) return null;

  if (loading && !previousData) {
    return LOADING_MESSAGE;
  }

  // react lint complains unless you do event handling pass down like this
  return (
    <ProductPageContext.Provider value={ProductPageState}>
      {hasProductMfeSeoSchema && (
      <SeoSchema
        productDetail={ProductPageState.collectionData?.activeProduct}
        seoReviewsData={ProductPageState.reviewData?.seoReviewsData}
        currency={currency}
      />
      )}
      {renderMfeReviewScript && (<ReviewScript />)}
      <div
        className="product-page-v7000"
        data-testid="product-page-v7000"
        data-collectionid={collectionId}
        data-productid={ProductPageState.collectionData?.activeProduct.productId || productId}
        data-kic={ProductPageState.collectionData?.activeProduct.kicId}
        data-currency={currency}
      >
        {ProductPageState?.collectionData?.breadcrumbsUrls && (
          <BreadCrumbsList breadCrumbs={ProductPageState?.collectionData?.breadcrumbsUrls} />
        )}

        <div className="product-page-content-container">
          {hasCategoryRecs && isExternalUser && isMobile && (
            <BubbleRecommendations className="bubble-recommendations-container" titleWrapper={titleWrapper} />
          )}

          <div className="product-page-wrapper">
            <div className="mobile-view" data-testid="mobile-name-and-description-wrapper">
              <ProductNameAndDescription />
            </div>
            <div className="product-page-images__container">
              {(!isMobile && isBrowser) && (
                <MeasurementsAndStyliticsOverlay
                  brand={brand}
                  tmntData={ProductPageState.tmntData}
                  activeProduct={ProductPageState.collectionData?.activeProduct}
                  hasPDPMFEBelowTheFold={hasPDPMFEBelowTheFold}
                />
              )}
              <MainImages
                isMobile={isMobile}
              />
            </div>
            <section className="mfe-product-page__info-container">
              <AddToBagForm
                swatchChanged={swatchChanged}
                categoryId={ProductPageState.categoryId}
                handlePrimarySizeUpdate={setSelectedPrimarySize}
                handleSecondarySizeUpdate={setSelectedSecondarySize}
                handleStickyPrimarySizeUpdate={setStickySelectedPrimarySize}
                handleStickySecondarySizeUpdate={setStickySelectedSecondarySize}
                reviewData={ProductPageState.reviewData}
                shippingContent={ProductPageState.shippingContent}
                siteRegion={siteRegion}
                eSpotContent={ProductPageState.content}
                setSizeGuideOpen={setSizeGuideOpen}
                brand={brand}
                hasPDPMFEBelowTheFold={hasPDPMFEBelowTheFold}
              />

              {ProductPageState.collectionData?.activeProduct && (
                <SizeGuideModal
                  openSizeGuide={openSizeGuide}
                  eSpotContent={ProductPageState.eSpotContent}
                  setSizeGuideOpen={setSizeGuideOpen}
                />
              )}
            </section>
          </div>
        </div>
        <ESpotContent eSpotData={ProductPageState.content?.pdpESpots?.pdp_belowthefold1} />
        {hasPDPMFEBelowTheFold && (
          <>
            {styliticsComponent}
            {hasProductRecsStoreAttr && (
              <div className="mfe-recommendation-sliders" data-testid="mfe-recommendation-sliders">
                <MicroFrontend
                  client="recommendationSliders"
                  mfe="CUSTOMER"
                  queryString="placements=item_page.rr1|item_page.rr2|item_page.rr3|item_page.rr4|item_page.rr5|item_page.rr6&clientId=cdp-pdp-1"
                />
              </div>
            )}
            {hasBaazarVoice && (
              <>
                <section
                  id="rating-and-reviews"
                  className="product-page__reviews-container scope-1892"
                  data-testid="mfe-rating-and-reviews"
                >
                  <ProductRatingReviews
                    reviewSku={ProductPageState.reviewData?.reviewsSku}
                  />
                </section>
                {showReviewsLegalText && (
                  <ReviewsLegalText
                    tmntData={ProductPageState?.tmntData}
                    customerServiceUrl={queryData?.config?.customerServiceUrl}
                  />
                )}
              </>
            )}
            <ESpotContent eSpotData={ProductPageState.content?.pdpESpots?.pdp_belowthefold2} />
          </>
        )}
      </div>
    </ProductPageContext.Provider>
  );
}
